import { z } from 'zod';
import { ValidationError } from 'modules/templateEditor/common';
import { MutatedExperimentVariant } from '../takeover/types';
import { MutateVariantResponse } from './common';

export enum DeleteCardProgress {
  NOT_STARTED = 'initial',
  DELETING = 'deleting',
  ENDED = 'ended',
}

export type VariantCardContextState = {
  cardIndex: number;
  variant: MutatedExperimentVariant;
  templateErrors: ValidationError[];
  deletingCard: DeleteCardProgress;
  updatingCard: boolean;
  renamingCard: boolean;
  isCardSelected: boolean;
  isCardInEditMode: boolean;
  isCardInPreviewMode: boolean;
  deleteCard: () => Promise<void>;
  editCard: () => void;
  renameCard: (newVariantName: string) => Promise<void>;
  selectCard: () => void;
  updateCard: (
    variantFieldValues: VariantFieldValues,
    submitType: SubmitButtonType,
  ) => Promise<MutateVariantResponse>;
};

export enum VariantFormFieldKey {
  EMAIL_BODY = 'emailBody',
  EMAIL_FROM_NAME = 'emailFromName',
  EMAIL_PRE_HEADER = 'emailPreheaderText',
  EMAIL_SENDERS_EMAIL = 'emailSendersEmail',
  EMAIL_REPLY_TO_EMAIL = 'emailReplyToEmail',
  EMAIL_SUBJECT_LINE = 'emailSubjectLine',
  PUSH_TITLE = 'pushTitle',
  PUSH_MESSAGE = 'pushMessage',
  SMS_MESSAGE = 'smsMessage',
  SMS_IMAGE_URL = 'smsMessageUrl',
  WEB_PUSH_TITLE = 'webPushTitle',
  WEB_PUSH_BODY = 'webPushBody',
  WEB_PUSH_ICON = 'webPushIcon',
  WEB_PUSH_CLICK_LOCATION = 'webPushClickLocation',
  SEND_TIME_DATE = 'sendTimeDate',
  SEND_TIME_TIME = 'sendTimeTime',
  SEND_TIME_VALIDATION_META_INFO = 'sendTimeValidationMetaInfo',
  STO = 'SendTimeOptimization',
  EVERYTHING = 'everything',
}

export enum VariantComboFormFieldKey {
  EMAIL_SENDERS = 'emailSendersCombo',
  WEB_PUSH_TITLE_BODY = 'webPushTitleAndBodyCombo',
  SEND_TIME = 'sendTimeCombo',
}

export enum VariantFormFieldType {
  TEXT = 'text',
  /** More than one fields whose values depends on each other */
  COMBO = 'combo',
  NON_EDITABLE = 'useTemplateEditor',
  STO = 'STO',
}

export interface CommonVariantFormFieldsProps {
  /** Unique identifier for the variant form field */
  key: VariantFormFieldKey;
  /** Translated label for the form field header */
  label: string;
  /** Type of input form field to be rendered */
  type: VariantFormFieldType;
  /** Should the form field be focused */
  isFocused?: boolean;
}

type CommonVariantFormFieldsPropsWithoutLabel = Omit<CommonVariantFormFieldsProps, 'label'>;
interface CommonVariantComboFormFieldsProps
  extends Omit<CommonVariantFormFieldsPropsWithoutLabel, 'key'> {
  /** Unique identifier for the combo form field */
  key: VariantComboFormFieldKey;
  type: VariantFormFieldType.COMBO;
  /** The variant field collection which forms the combo */
  fields: Exclude<VariantFormField, VariantComboFormField>[];
}

export interface TextFormFieldCombo extends CommonVariantComboFormFieldsProps {
  fields: VariantTextFormField[];
}

export interface VariantTextFormField extends CommonVariantFormFieldsProps {
  type: VariantFormFieldType.TEXT;
}

export interface WebPushTitleBodyFormField extends TextFormFieldCombo {
  key: VariantComboFormFieldKey.WEB_PUSH_TITLE_BODY;
}

export interface EmailSendersFormField extends TextFormFieldCombo {
  key: VariantComboFormFieldKey.EMAIL_SENDERS;
}

export interface SendTimeFormField extends TextFormFieldCombo {
  key: VariantComboFormFieldKey.SEND_TIME;
}

export interface VariantNonEditableFormField extends CommonVariantFormFieldsProps {
  type: VariantFormFieldType.NON_EDITABLE;
}

export interface VariantSTOFormField extends CommonVariantFormFieldsPropsWithoutLabel {
  type: VariantFormFieldType.STO;
}

export type VariantComboFormField =
  | WebPushTitleBodyFormField
  | EmailSendersFormField
  | SendTimeFormField;

export type VariantFormField =
  | VariantTextFormField
  | VariantComboFormField
  | VariantSTOFormField
  | VariantNonEditableFormField;

export type VariantFieldValues = {
  [x in VariantFormFieldKey]?: any;
};

export type VariantZodSchemaRecord = {
  [x in VariantFormFieldKey]?: z.ZodTypeAny;
};

export type VariantZodSchema = z.Schema<any, any>;

export type VariantFormFieldSchemaReturnType = {
  formFieldSchema: VariantFormField[];
  formFieldZodSchema: VariantZodSchema;
};

export type VariantFormFieldValueReturnType = VariantFieldValues;

export enum SubmitButtonType {
  SIMPLY_SUBMIT = 'simply_add',
  SUBMIT_AND_ADD_VARIANT = 'submit_and_add_variant',
}
